import { EON_VIDEO_ID, GameLaunchStatus } from "@onmo/eon-streaming";
import { LoadingStatuses } from "../constants/gamePlay";
import { TOAST_MESSAGE_VIDEO_IDS } from "../constants/videos";
import { getGuideVideoUrl } from "./guideVideos";
import { StreamErrorType } from "../types/gamePlay";
import { NetworkConditionStatus, NetworkQuality } from "../types/network";
import BadNetwork from "../assets/icons/bad-network.svg";
import MediumNetwork from "../assets/icons/medium-network.svg";
import GoodNetwork from "../assets/icons/good-network.svg";

export const getLoadingGameMessage = (
  loadingStatus?: LoadingStatuses,
  showLaunching?: boolean,
) => {
  if (showLaunching && loadingStatus === "Launching") {
    return {
      type: "snackbar",
      title: "Your Game Is Installed",
      icon: "icon-bold-tick-circle",
      status: GameLaunchStatus.NONE,
    };
  } else if (loadingStatus === "Done") {
    return {
      type: "snackbar",
      title: "Your Console is Ready!",
      icon: "icon-bold-tick-circle",
      status: GameLaunchStatus.NONE,
    };
  }
};

const getBaseMessage = (status: GameLaunchStatus) => {
  switch (status) {
    case GameLaunchStatus.STATUS_INSTALLING:
      return {
        type: "snackbar",
        title: "Console Ready",
        icon: "icon-bold-tick-circle",
        status,
      };

    case GameLaunchStatus.STATUS_STEAMLOGIN:
      return {
        type: "snackbar",
        title: "Steam Login",
        description: "Select profile or login to Steam",
        status,
      };

    case GameLaunchStatus.STATUS_SETLAUNCHEROPTIONS:
      return {
        type: "snackbar",
        title: "Optimizing Console...",
        icon: "icon-loading_circle",
        status,
      };

    case GameLaunchStatus.STATUS_LAUNCHER_UP:
      return {
        type: "snackbar",
        title: "Game Launcher Active",
        description: "Check and interact if needed to continue",
        status,
      };

    case GameLaunchStatus.STATUS_EULA:
      return {
        type: "snackbar",
        title: "EULA",
        description: "Accept End User License Agreement",
        status,
      };

    case GameLaunchStatus.STATUS_NOGAMELICENSE:
      return {
        type: "popup",
        title: "License Required",
        description: "You don’t own this game. Purchase it to start playing.",
        status,
      };

    case GameLaunchStatus.STATUS_CONSENTNOCLOUDSYNC:
      return {
        type: "snackbar",
        title: "Cloud Sync Issue",
        description: "User Input Needed",
        status,
      };

    case GameLaunchStatus.STATUS_GAMELAUNCH_TIMEOUT:
      return {
        type: "popup",
        title: "Session Timed Out",
        description:
          "The game didn’t start within the expected time and has been closed. Please try again.",
        status,
      };

    case GameLaunchStatus.STATUS_GAMELAUNCHING:
      return {
        type: "snackbar",
        title: "Launching Game...",
        icon: "icon-loading_circle",
        description: "Configuring your Smart Console and loading game assets",
        status,
      };

    case GameLaunchStatus.STATUS_GAMEEXITED:
      return {
        type: "popup",
        title: "Game Closed",
        description:
          "You’ve exited the game. Why not try something else from the library?",
        status,
      };

    case GameLaunchStatus.ERR_NOTACTIVE:
      return {
        type: "popup",
        title: "Game Closed",
        description:
          "The game was closed due to inactivity. Ready to play? Jump back in or try another title.",
        status,
      };

    case GameLaunchStatus.STATUS_SYNCONEXIT:
      return {
        type: "snackbar",
        title: "Shutting Down Console...",
        icon: "icon-loading_circle",
        status,
      };

    case GameLaunchStatus.ERROR:
      return {
        type: "popup",
        title: "Session Inactive",
        description: "Your session has ended. Please restart the game!",
        status,
      };

    case GameLaunchStatus.NETWORK_ERROR:
      return {
        type: "snackbar",
        title: "Network Unstable",
        description:
          "Check your network connection or try to restart the game session",
        icon: "icon-network-alert",
        status,
      };

    case GameLaunchStatus.NETWORK_DISCONNECTED:
      return {
        type: "snackbar",
        title: "Network Disconnected",
        description: "You're offline. Check your Wi-Fi or cable.",
        icon: "icon-network-alert",
        status,
      };

    case GameLaunchStatus.STATUS_STEAMLOGIN_SUCCESS:
      return {
        type: "snackbar",
        title: "Steam Login Success!",
        icon: "icon-bold-tick-circle",
        status,
      };

    case GameLaunchStatus.STATUS_GAMERUNNING_UPDATEPENDING:
      return {
        type: "snackbar",
        title: "Maintenance Alert",
        description: "Shutting down in 5 minutes, please save\nand exit now",
        status,
      };

    case GameLaunchStatus.STATUS_UPDATEDETECTED:
      return {
        type: "popup",
        title: "Update Required",
        description:
          "Steam started an update — your session was closed. Please relaunch the game after the update completes.",
        status,
      };

    case GameLaunchStatus.STATUS_GAMERUNNING:
      return {
        type: "snackbar",
        title: "Game is Ready",
        icon: "icon-bold-tick-circle",
        description: "Access Console Options anytime — learn how.",
        status,
      };

    case GameLaunchStatus.REMOTE_INPUT_REQUIRED:
      return {
        type: "snackbar",
        title: "Put Aside Your Controller",
        description: "Use TV remote control to use the keyboard",
        status,
      };

    case GameLaunchStatus.STATUS_INACTIVITY_TIMEOUT:
      return {
        type: "snackbar",
        title: "Inactivity Warning!",
        description:
          "Your session is about to be terminated.\nPress anything to continue playing",
        icon: "icon-bold-danger",
        status,
      };
  }
};

export const getNetworkConditionMessage = (status: NetworkConditionStatus) => {
  switch (status) {
    case NetworkConditionStatus.PACKET_LOSS:
      return {
        type: "snackbar",
        title: "Packet Loss",
        description:
          "Connection is dropping. You may see stutters or glitches.",
        icon: "icon-packet-lost",
        status,
      };
    case NetworkConditionStatus.LATENCY_VARIATION:
      return {
        type: "snackbar",
        title: "Latency Variation",
        description:
          "Unstable connection. Try a wired connection or move closer to your router.",
        icon: "icon-latency-variation",
        status,
      };
    case NetworkConditionStatus.HIGH_LATENCY:
      return {
        type: "snackbar",
        title: "High Latency",
        description:
          "Slow connection. Try a wired connection or move closer to your router.",
        icon: "icon-high-latency",
        status,
      };
  }
};

export const getPcGameMessage = (status: GameLaunchStatus) => {
  const baseMessage = getBaseMessage(status);

  if (!baseMessage) return;

  const videoURL = getGuideVideoUrl(TOAST_MESSAGE_VIDEO_IDS[status]);
  return videoURL ? { ...baseMessage, videoURL } : baseMessage;
};

export const getStreamErrorMessage = (streamError?: StreamErrorType) => {
  switch (streamError) {
    case "playing":
      return {
        title: "Another Game is Running",
        description:
          "You can only play one game at a time. Starting this game will automatically close your previous session.",
      };
    case "exit":
      return {
        title: "Game Closed",
        description:
          "You’ve exited the game. Why not try something else from the library?",
      };
    case "err_updatinggame":
      return {
        title: "Maintenance",
        description:
          "This game is currently under maintenance. Please try again later.",
      };
    case "server_error":
      return {
        title: "Servers Packed",
        description:
          "All our servers are full right now—hang tight and try again soon!",
      };
    case "sync_in_progress":
      return {
        title: "Saving Your Progress",
        description:
          "Your latest save is being synced. Please wait for a few minutes before launching this game again or try a different game.",
      };
    case "payment_failed":
    case "error":
    default:
      return {
        title: "Unable to Load Game",
        description:
          "We encountered an issue loading your game. Please restart and try again. If the problem persists, check your connection and try later.",
      };
  }
};

export const requestPointerLockForPCGame = () => {
  if (!document.pointerLockElement) {
    const pcStreamVideo = document.getElementById(EON_VIDEO_ID);
    if (pcStreamVideo) {
      try {
        pcStreamVideo.requestPointerLock();
        console.log("Pointer lock activated");
      } catch (error) {
        console.error("Error request pointer lock:", error);
      }
    }
  }
};

export const exitPointerLock = () => {
  try {
    if (document.pointerLockElement) {
      document.exitPointerLock();
      console.log("Pointer lock deactivated");
    }
  } catch (error) {
    console.error("Error exiting pointer lock:", error);
  }
};

export const getNetworkIcon = (
  networkQuality: NetworkQuality,
  isNetworkDisconnected?: boolean,
) => {
  if (isNetworkDisconnected) {
    return BadNetwork;
  }

  switch (networkQuality) {
    case NetworkQuality.Good:
      return GoodNetwork;
    case NetworkQuality.Medium:
      return MediumNetwork;
    case NetworkQuality.Bad:
      return BadNetwork;
    default:
      return GoodNetwork;
  }
};

export const getLatencyQuality = (
  latency: number | undefined,
  isNetworkDisconnected?: boolean,
) => {
  if (isNetworkDisconnected) return NetworkQuality.Bad;
  if (latency === undefined) return NetworkQuality.Bad;
  if (latency < 150) return NetworkQuality.Good;
  if (latency <= 250) return NetworkQuality.Medium;
  return NetworkQuality.Bad;
};
